import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { PlusCircle, Edit, Trash2, Coins, Zap, TrendingUp, Settings, Wand2, Box, Video, Layers, Eye, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

import aiTaskCostService from '../../services/aiTaskCostService';
import { AITaskCost, AITaskCostCreate, AITaskCostUpdate } from '../../schemas/aiTaskCost';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup, // Import SelectGroup
  SelectLabel, // Import SelectLabel
} from '../../components/ui/select';

// Define AITaskType enum values using clean names as values
const AITaskTypeOptions = [
  {
    label: "1. Ad Studio - Image Generation",
    options: [
      { value: "Image Generation (Basic)", label: "Image Generation (Basic)" },
    ]
  },
  {
    label: "2. Ad Studio - Image Generation with Brandify",
    options: [
      { value: "Image Generation with Brandify", label: "Image Generation with Brandify" },
    ]
  },
  {
    label: "3. Ad Studio AR - Image to 3D Model",
    options: [
      { value: "Image to 3D Model Generation", label: "Image to 3D Model Generation" },
    ]
  },
  {
    label: "4. Ad Studio AR - 3D Model to AR Ad",
    options: [
      { value: "3D Model to AR Ad Generation", label: "3D Model to AR Ad Generation" },
    ]
  },
  {
    label: "5. Ad Studio Video - Image to Video",
    options: [
      { value: "Image to Video Generation", label: "Image to Video Generation" },
    ]
  },
  {
    label: "6. Post Publishing",
    options: [
      { value: "AI-Assisted Post Creation", label: "AI-Assisted Post Creation" },
    ]
  },
  {
    label: "7. Post Scheduling",
    options: [
      { value: "Automated Post Scheduling", label: "Automated Post Scheduling" },
    ]
  },
  {
    label: "Additional Services",
    options: [
      { value: "image_enhancement", label: "Image Enhancement" },
      { value: "image_analysis", label: "Image Analysis" },
      { value: "three_d_generation", label: "3D Generation (Legacy)" },
    ]
  },
];

const AITaskCostsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<AITaskCost | null>(null);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<AITaskCostCreate | AITaskCostUpdate>();

  const { data: aiTaskCosts, isLoading, error } = useQuery<AITaskCost[]> ({
    queryKey: ['aiTaskCosts'],
    queryFn: aiTaskCostService.getAllAITaskCosts,
  });

  const createMutation = useMutation({
    mutationFn: aiTaskCostService.createAITaskCost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiTaskCosts'] });
      toast.success('AI Task Cost created successfully!');
      setIsFormOpen(false);
      reset();
    },
    onError: (err: any) => {
      toast.error(`Error creating AI Task Cost: ${err.response?.data?.detail || err.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ taskType, data }: { taskType: string; data: AITaskCostUpdate }) =>
      aiTaskCostService.updateAITaskCost(taskType, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiTaskCosts'] });
      toast.success('AI Task Cost updated successfully!');
      setIsFormOpen(false);
      setEditingCost(null);
      reset();
    },
    onError: (err: any) => {
      toast.error(`Error updating AI Task Cost: ${err.response?.data?.detail || err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: aiTaskCostService.deleteAITaskCost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiTaskCosts'] });
      toast.success('AI Task Cost deleted successfully!');
    },
    onError: (err: any) => {
      toast.error(`Error deleting AI Task Cost: ${err.response?.data?.detail || err.message}`);
    },
  });

  const onSubmit = (data: AITaskCostCreate | AITaskCostUpdate) => {
    if (editingCost) {
      updateMutation.mutate({ taskType: editingCost.task_type, data });
    } else {
      createMutation.mutate(data as AITaskCostCreate);
    }
  };

  const handleEditClick = (cost: AITaskCost) => {
    setEditingCost(cost);
    setValue('task_type', cost.task_type); // Set value for select
    setValue('cost_in_credits', cost.cost_in_credits);
    setValue('display_text', cost.display_text || '');
    setValue('show_in_pricing', cost.show_in_pricing !== false); // Default to true if undefined
    setIsFormOpen(true);
  };

  const handleDeleteClick = (taskType: string) => {
    if (window.confirm(`Are you sure you want to delete the cost for ${taskType}?`)) {
      deleteMutation.mutate(taskType);
    }
  };

  const handleNewCostClick = () => {
    setEditingCost(null);
    reset();
    setIsFormOpen(true);
  };

  // Helper function to get task type icon and color
  const getTaskTypeVisuals = (taskType: string) => {
    switch (taskType) {
      case 'text_to_image': return { Icon: Wand2, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-500/10', label: 'Ad Studio - Image' };
      case 'image_processing': return { Icon: Layers, color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10', label: 'Ad Studio - Brandify' };
      case 'image_enhancement': return { Icon: Layers, color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10', label: 'Image Enhancement' };
      case 'image_to_3d': return { Icon: Box, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10', label: 'Ad Studio AR - 3D' };
      case 'three_d_to_ar_ad': return { Icon: Box, color: 'text-teal-600 dark:text-teal-400', bgColor: 'bg-teal-500/10', label: 'Ad Studio AR - AR Ad' };
      case 'video_generation': return { Icon: Video, color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-500/10', label: 'Ad Studio - Video' };
      case 'content_generation': return { Icon: Wand2, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10', label: 'Content Generation' };
      case 'AI-Assisted Post Creation': return { Icon: Wand2, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10', label: 'AI-Assisted Post Creation' };
      case 'post_scheduling': return { Icon: Clock, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10', label: 'Post Scheduling' };
      case 'image_analysis': return { Icon: Eye, color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-500/10', label: 'Image Analysis' };
      default: return { Icon: Zap, color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-500/10', label: taskType };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      {/* Modern Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <motion.div 
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                <Coins className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black bg-gradient-to-r from-gray-900 via-amber-600 to-orange-600 bg-clip-text text-transparent dark:from-white dark:via-amber-300 dark:to-orange-300">
                  AI Task Costs
                </h1>
                <p className="text-slate-600 dark:text-slate-300">Manage credit costs for AI-powered services</p>
              </div>
            </div>
            
            <Button 
              onClick={handleNewCostClick} 
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-amber-500/25 transition-all duration-300"
            >
              <PlusCircle className="mr-2 h-5 w-5" /> 
              Add New Cost
            </Button>
          </motion.div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin"></div>
            <span className="ml-3 text-slate-600 dark:text-slate-400">Loading AI task costs...</span>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center">
            <p className="text-red-600 dark:text-red-400 font-medium">Error: {error.message}</p>
          </div>
        )}

        {!isLoading && !error && aiTaskCosts && (
          <motion.div 
            className="relative overflow-hidden rounded-3xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-indigo-50/30 to-purple-50/30 dark:from-indigo-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
            
            {/* Table Header */}
            <div className="relative border-b border-slate-200/60 dark:border-slate-600/40 px-6 py-4">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-5 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  AI Service
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Credit Cost
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Show in Pricing
                </div>
                <div className="col-span-3 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-right">
                  Actions
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="relative">
              {aiTaskCosts.map((cost, index) => {
                const { Icon, color, bgColor, label } = getTaskTypeVisuals(cost.task_type);
                const costValue = cost.cost_in_credits % 1 === 0 ? cost.cost_in_credits.toString() : cost.cost_in_credits.toFixed(1);
                
                return (
                  <motion.div
                    key={cost.task_type}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="group relative border-b border-slate-200/40 dark:border-slate-600/30 last:border-b-0 hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-all duration-200"
                  >
                    <div className="px-6 py-5">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        {/* Service Info */}
                        <div className="col-span-5 flex items-center space-x-4">
                          <div className={`w-10 h-10 ${bgColor} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200`}>
                            <Icon className={`w-5 h-5 ${color}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">
                              {label}
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">
                              {cost.task_type}
                            </p>
                          </div>
                        </div>

                        {/* Cost */}
                        <div className="col-span-2 flex justify-center">
                          <div className="flex items-center space-x-2 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-xl">
                            <Coins className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                              {costValue}
                            </span>
                            <span className="text-xs text-amber-600/70 dark:text-amber-400/70">
                              credits
                            </span>
                          </div>
                        </div>

                        {/* Show in Pricing Toggle */}
                        <div className="col-span-2 flex justify-center">
                          <div className={`flex items-center space-x-2 px-3 py-2 rounded-xl ${
                            (cost.show_in_pricing !== false) // Handle undefined as true for backward compatibility
                              ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' 
                              : 'bg-gray-50 dark:bg-gray-900/20 text-gray-500 dark:text-gray-400'
                          }`}>
                            <div className={`w-2 h-2 rounded-full ${
                              (cost.show_in_pricing !== false) ? 'bg-green-500' : 'bg-gray-400'
                            }`}></div>
                            <span className="text-sm font-medium">
                              {(cost.show_in_pricing !== false) ? 'Visible' : 'Hidden'}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="col-span-3 flex items-center justify-end space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditClick(cost)}
                            className="opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-slate-100 dark:hover:bg-slate-700 hover:scale-105"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteClick(cost.task_type)}
                            className="opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:scale-105"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-2xl">
          <DialogHeader className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <Coins className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-slate-800 dark:text-slate-100">
                  {editingCost ? 'Edit AI Task Cost' : 'Add New AI Task Cost'}
                </DialogTitle>
                <DialogDescription className="text-slate-600 dark:text-slate-400">
                  {editingCost ? 'Update the credit cost for this AI service.' : 'Set the credit cost for a new AI service.'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="task_type" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  AI Service Type
                </Label>
                <Select
                  onValueChange={(value) => setValue('task_type', value)}
                  value={editingCost?.task_type || undefined}
                  disabled={!!editingCost}
                >
                  <SelectTrigger className="w-full bg-white/50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 dark:focus:border-amber-400">
                    <SelectValue placeholder="Choose an AI service type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40">
                    {AITaskTypeOptions.map((group, index) => (
                      <SelectGroup key={index}>
                        <SelectLabel className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-2 py-1">
                          {group.label}
                        </SelectLabel>
                        {group.options.map((option) => (
                          <SelectItem 
                            key={option.value} 
                            value={option.value}
                            className="hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                {errors.task_type && (
                  <p className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.task_type.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cost_in_credits" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Credit Cost
                </Label>
                <div className="relative">
                  <Coins className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-amber-500" />
                  <Input
                    id="cost_in_credits"
                    type="number"
                    step="0.1"
                    placeholder="Enter credit cost"
                    {...register('cost_in_credits', {
                      required: 'Cost is required',
                      valueAsNumber: true,
                      min: { value: 0, message: 'Cost cannot be negative' },
                    })}
                    className="pl-10 bg-white/50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 dark:focus:border-amber-400"
                  />
                </div>
                {errors.cost_in_credits && (
                  <p className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.cost_in_credits.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_text" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Display Text (for pricing page)
                </Label>
                <div className="relative">
                  <input
                    id="display_text"
                    type="text"
                    placeholder="e.g., 'Create stunning images'"
                    {...register('display_text')}
                    className="w-full bg-white/50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 dark:focus:border-amber-400 px-4 py-3"
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Custom text shown in pricing page. Leave empty to use auto-generated text.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="show_in_pricing" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Display Settings
                </Label>
                <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600">
                  <input
                    id="show_in_pricing"
                    type="checkbox"
                    {...register('show_in_pricing')}
                    className="w-4 h-4 text-amber-600 bg-white border-slate-300 rounded focus:ring-amber-500 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
                  />
                  <div className="flex-1">
                    <label htmlFor="show_in_pricing" className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                      Show in pricing page credit explanation
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      When enabled, this task will appear in the "How Credits Work" section on the pricing page
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="flex items-center space-x-3 pt-6 border-t border-slate-200/60 dark:border-slate-600/40">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsFormOpen(false)}
                className="border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending || updateMutation.isPending}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-amber-500/25 transition-all duration-300"
              >
                {editingCost ? (updateMutation.isPending ? 'Saving...' : 'Save Changes') : (createMutation.isPending ? 'Creating...' : 'Create Cost')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AITaskCostsPage;